import { useEffect } from 'react';
import { Star, TrendingUp, Clock, AlertTriangle, MessageSquare, Share2, LineChart as LineChartIcon } from 'lucide-react';
import { Sparkline } from '@/components/Sparkline';
import { KPI_METRICS } from './kpiMetrics';

/**
 * Determina a cor semântica da tendência com base nos últimos 2 pontos do histórico.
 * Retorna hex color: verde (bom), vermelho (ruim), cinza (flat / sem dados).
 */
const getTrendColor = (values, lowerIsBetter) => {
  const filtered = values.filter((v) => typeof v === 'number');
  if (filtered.length < 2) return '#94A3B8';
  const diff = filtered[filtered.length - 1] - filtered[filtered.length - 2];
  if (Math.abs(diff) < 0.01) return '#94A3B8';
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  return good ? '#10B981' : '#EF4444';
};

/**
 * Wrapper de card clicável que abre o modal de histórico.
 */
const ClickableKPI = ({ metric, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]/60 focus-visible:ring-offset-2 rounded-xl"
    data-testid={testId}
    aria-label={`Ver histórico de ${metric.label}`}
  >
    {children}
  </button>
);

export const DashboardKPIs = ({
  stats,
  theme,
  monthlyEvolution = [],
  loadMonthlyEvolution,
  openHistory,
}) => {
  const { cardBg, textPrimary, textSecondary, textMuted } = theme;

  // Carrega histórico mensal uma vez ao montar (usado para sparklines E para o modal)
  useEffect(() => {
    if (loadMonthlyEvolution && (!monthlyEvolution || monthlyEvolution.length === 0)) {
      loadMonthlyEvolution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Convidados ao Google — mantém a lógica de cor existente
  const pubRate = stats?.public_recommendation_rate || 0;
  const pubCount = stats?.public_recommendation_count || 0;
  const total = stats?.total_feedbacks || 0;
  let pubColor = 'text-slate-400';
  let pubLabel = '—';
  if (total > 0) {
    if (pubRate >= 80) { pubColor = 'text-emerald-500'; pubLabel = 'Excelente'; }
    else if (pubRate >= 60) { pubColor = 'text-amber-500'; pubLabel = 'Saudável'; }
    else { pubColor = 'text-red-500'; pubLabel = 'Atenção'; }
  }

  // Helper: extrai série de valores de uma métrica ao longo dos meses
  const seriesOf = (metricKey) => {
    const m = KPI_METRICS[metricKey];
    return monthlyEvolution.map((month) => m.getValue(month));
  };

  const cardBase = `rounded-xl p-3 sm:p-5 border shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${cardBg}`;

  // Sparkline slot mostrado ao pé de cada card KPI
  const SparklineRow = ({ metricKey }) => {
    const values = seriesOf(metricKey);
    const color = getTrendColor(values, KPI_METRICS[metricKey].lowerIsBetter);
    return (
      <div className="mt-3 flex items-center gap-1.5" data-testid={`sparkline-${metricKey}`}>
        <LineChartIcon className="w-3 h-3 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <Sparkline values={values} color={color} height={22} />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* KPI Cards principais */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-5 mb-4 sm:mb-6">
        <ClickableKPI metric={KPI_METRICS.rating} onClick={() => openHistory('rating')} testId="kpi-rating">
          <div className={cardBase}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${textSecondary}`}>Média Geral</p>
                <p className={`text-2xl sm:text-3xl font-bold ${textPrimary} mt-1`}>{stats?.average_rating ?? (stats?.retention?.retained ? '—' : 0)}</p>
                <p className={`text-[10px] sm:text-xs ${textMuted} mt-1`}>nota média dos clientes</p>
              </div>
              <div className="icon-container icon-gold"><Star className="w-5 h-5 sm:w-6 sm:h-6" /></div>
            </div>
            <SparklineRow metricKey="rating" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.google} onClick={() => openHistory('google')} testId="kpi-public-recommendation">
          <div className={cardBase}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${textSecondary} truncate`} title="Clientes satisfeitos convidados a também avaliar sua empresa no Google">Convidados ao Google</p>
                <p className={`text-2xl sm:text-3xl font-bold ${pubColor} mt-1`}>{pubRate}%</p>
                <p className={`text-[10px] sm:text-xs ${textMuted} mt-1 truncate`}>{total > 0 ? `${pubCount}/${total} satisfeitos · ${pubLabel}` : 'aguardando dados'}</p>
              </div>
              <div className="icon-container icon-emerald"><Share2 className="w-5 h-5 sm:w-6 sm:h-6" /></div>
            </div>
            <SparklineRow metricKey="google" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.satisfaction} onClick={() => openHistory('satisfaction')} testId="kpi-satisfaction">
          <div className={cardBase}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${textSecondary}`}>Satisfação</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-500 mt-1">{stats?.satisfaction_percentage ?? (stats?.retention?.retained ? '—' : 0)}%</p>
                <p className={`text-[10px] sm:text-xs ${textMuted} mt-1`}>saldo elogios x críticas</p>
              </div>
              <div className="icon-container icon-emerald"><TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" /></div>
            </div>
            <SparklineRow metricKey="satisfaction" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.responseTime} onClick={() => openHistory('responseTime')} testId="kpi-response-time">
          <div className={cardBase}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${textSecondary}`}>Tempo de Resposta</p>
                <p className={`text-2xl sm:text-3xl font-bold ${textPrimary} mt-1`}>
                  {stats?.avg_response_time_hours ? `${stats.avg_response_time_hours}h` : '--'}
                </p>
                <p className={`text-[10px] sm:text-xs ${textMuted} mt-1`}>média para responder críticas</p>
              </div>
              <div className="icon-container icon-blue"><Clock className="w-5 h-5 sm:w-6 sm:h-6" /></div>
            </div>
            <SparklineRow metricKey="responseTime" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.urgent} onClick={() => openHistory('urgent')} testId="kpi-urgent">
          <div className={cardBase}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${textSecondary}`}>Urgentes</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-500 mt-1">{stats?.urgent_count || 0}</p>
                <p className={`text-[10px] sm:text-xs ${textMuted} mt-1`}>exigem sua ação imediata</p>
              </div>
              <div className="icon-container icon-red"><AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" /></div>
            </div>
            <SparklineRow metricKey="urgent" />
          </div>
        </ClickableKPI>
      </div>

      {/* Summary Cards (Críticas, Sugestões, Elogios) */}
      <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-4 sm:mb-6">
        <ClickableKPI metric={KPI_METRICS.complaints} onClick={() => openHistory('complaints')} testId="kpi-complaints">
          <div className={`${cardBase} border-l-4 border-l-red-500`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-[10px] sm:text-sm font-medium ${textSecondary}`}>Críticas</p>
                <p className={`text-lg sm:text-2xl font-bold ${textPrimary}`}>{stats?.complaints_count || 0}</p>
              </div>
              <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 hidden sm:block" />
            </div>
            <SparklineRow metricKey="complaints" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.suggestions} onClick={() => openHistory('suggestions')} testId="kpi-suggestions">
          <div className={`${cardBase} border-l-4 border-l-blue-500`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-[10px] sm:text-sm font-medium ${textSecondary}`}>Sugestões</p>
                <p className={`text-lg sm:text-2xl font-bold ${textPrimary}`}>{stats?.suggestions_count || 0}</p>
              </div>
              <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 hidden sm:block" />
            </div>
            <SparklineRow metricKey="suggestions" />
          </div>
        </ClickableKPI>

        <ClickableKPI metric={KPI_METRICS.praises} onClick={() => openHistory('praises')} testId="kpi-praises">
          <div className={`${cardBase} border-l-4 border-l-amber-500`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-[10px] sm:text-sm font-medium ${textSecondary}`}>Elogios</p>
                <p className={`text-lg sm:text-2xl font-bold ${textPrimary}`}>{stats?.praises_count || 0}</p>
              </div>
              <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400 hidden sm:block" />
            </div>
            <SparklineRow metricKey="praises" />
          </div>
        </ClickableKPI>
      </div>

      {/* Hint discreto de descoberta */}
      <p className={`text-[11px] mb-4 -mt-2 ${textMuted} flex items-center gap-1.5`} data-testid="kpi-history-hint">
        <LineChartIcon className="w-3 h-3" />
        Toque em qualquer indicador acima para ver a evolução dos últimos 6 meses.
      </p>
    </>
  );
};
